import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import SettingsSidebarLayout from '@/components/ui/settings-sidebar-layout';
import type { SettingsSideNavItem } from '@/components/ui/settings-side-nav';
import {
  loadDateLocaleSettings,
  saveDateLocaleSettings,
  TIMEZONE_OPTIONS,
  DATE_FORMAT_OPTIONS,
  DEFAULT_DATE_LOCALE,
  type DateLocaleSettings,
} from '@/lib/companySettings';
import { setDateLocaleCache } from '@/lib/dates';
import { useAppContext } from '@/contexts/AppContext';
import { Building2, Car, Clock3, Eye, EyeOff, Loader2, Save } from 'lucide-react';

const DEFAULT_TENANT_ID = '1225f4cb-43ae-463d-a6b0-53f721362dbf';

interface SettingRow {
  name: string;
  value: string | null;
  label: string | null;
  description: string | null;
  type: string;
}

// Rows whose name suggests a credential are masked by default -- same
// screen-hygiene convention as IvrGeneralSettings.tsx.
const isSecretField = (name: string) => /key|secret|sid|token/i.test(name);

const INFO_TAB = 'info';
const TAXICALLER_TAB = 'taxicaller';
const LOCALE_TAB = 'locale';

/** A tiny, independent-of-the-global-cache formatter for the live "right
 *  now" preview in the Locale tab, so it updates as the admin changes the
 *  dropdowns, before they've hit Save. */
const previewNow = (loc: DateLocaleSettings): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-NZ', {
      timeZone: loc.timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).formatToParts(new Date());
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
    const y = get('year'), m = get('month'), d = get('day');
    const h = get('hour'), min = get('minute'), ap = get('dayPeriod').toLowerCase();
    const datePart =
      loc.dateFormat === 'MM/DD/YYYY' ? `${m}/${d}/${y}` : loc.dateFormat === 'YYYY-MM-DD' ? `${y}-${m}-${d}` : `${d}/${m}/${y}`;
    return `${datePart}, ${h}:${min} ${ap}`;
  } catch {
    return '';
  }
};

const CompanySettings: React.FC = () => {
  const { updateAdminSettings } = useAppContext() as { updateAdminSettings: (u: { gstPercent: number }) => void };
  const [active, setActive] = useState<string>(INFO_TAB);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Company Info + TaxiCaller -- generic settings rows (TaxiCaller reuses the
  // exact same ivr.taxicaller_* rows IVR Settings edits, so there's one
  // source of truth, just reachable from a more obvious place).
  const [rows, setRows] = useState<SettingRow[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  // Locale
  const [locale, setLocale] = useState<DateLocaleSettings>(DEFAULT_DATE_LOCALE);
  const [savedLocale, setSavedLocale] = useState<DateLocaleSettings>(DEFAULT_DATE_LOCALE);
  const [localeSaving, setLocaleSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('name, value, label, description, type')
        .or(
          'name.eq.ivr.company_name,name.like.ivr.taxicaller_%,name.eq.company.company_number,name.eq.gst_percent,name.eq.company.gst_number',
        );
      if (error) throw error;
      const loaded = (data as SettingRow[]) || [];
      setRows(loaded);
      setValues(Object.fromEntries(loaded.map((r) => [r.name, r.value ?? ''])));

      const dl = await loadDateLocaleSettings(DEFAULT_TENANT_ID);
      setLocale(dl);
      setSavedLocale(dl);
    } catch (err) {
      console.error('Error loading company settings:', err);
      toast({ title: 'Error', description: 'Failed to load company settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const companyNumberRow = rows.find((r) => r.name === 'company.company_number');
  const companyNameRow = rows.find((r) => r.name === 'ivr.company_name');
  const gstPercentRow = rows.find((r) => r.name === 'gst_percent');
  const gstNumberRow = rows.find((r) => r.name === 'company.gst_number');
  const taxicallerRows = useMemo(() => rows.filter((r) => r.name.startsWith('ivr.taxicaller_')), [rows]);

  const infoDirty =
    (values['ivr.company_name'] ?? '') !== (companyNameRow?.value ?? '') ||
    (values['company.company_number'] ?? '') !== (companyNumberRow?.value ?? '') ||
    (values['gst_percent'] ?? '') !== (gstPercentRow?.value ?? '') ||
    (values['company.gst_number'] ?? '') !== (gstNumberRow?.value ?? '');
  const taxicallerDirty = taxicallerRows.some((r) => (values[r.name] ?? '') !== (r.value ?? ''));

  const saveGenericRow = async (row: SettingRow | undefined, name: string, label: string, description: string) => {
    if (row) {
      const { error } = await supabase.from('settings').update({ value: values[name] }).eq('name', name);
      if (error) throw error;
    } else {
      const { error } = await supabase.from('settings').insert([
        {
          name,
          value: values[name] ?? '',
          category: 'CompanySettings',
          label,
          description,
          type: 'text',
          sort_order: 10,
          is_active: true,
          tenant_id: DEFAULT_TENANT_ID,
        },
      ]);
      if (error) throw error;
    }
  };

  const handleSaveInfo = async () => {
    const gstPercentNum = parseFloat(values['gst_percent'] ?? '');
    if (values['gst_percent'] && (isNaN(gstPercentNum) || gstPercentNum < 0)) {
      toast({ title: 'Error', description: 'Enter a valid GST percentage.', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      await saveGenericRow(companyNameRow, 'ivr.company_name', 'Company Name', 'Shown across the app and on the IVR greeting.');
      await saveGenericRow(
        companyNumberRow,
        'company.company_number',
        'Company / NZBN Number',
        'Shown on payment summaries, claims and reports.',
      );
      await saveGenericRow(
        gstPercentRow,
        'gst_percent',
        'GST Percentage',
        'GST rate used in payroll calculations across the app.',
      );
      await saveGenericRow(
        gstNumberRow,
        'company.gst_number',
        'GST Number',
        'Your GST registration number, shown on claims and reports.',
      );
      // Payroll math reads gstPercent straight from AppContext state -- patch
      // it immediately so a saved change takes effect without a reload,
      // same as Payment Settings did for this field before it moved here.
      if (!isNaN(gstPercentNum)) updateAdminSettings({ gstPercent: gstPercentNum });
      toast({ title: 'Saved', description: 'Company info updated.' });
      await load();
    } catch (err) {
      toast({ title: 'Error', description: (err as Error)?.message || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTaxiCaller = async () => {
    setSaving(true);
    try {
      const changed = taxicallerRows.filter((r) => (values[r.name] ?? '') !== (r.value ?? ''));
      const results = await Promise.all(
        changed.map((r) => supabase.from('settings').update({ value: values[r.name] }).eq('name', r.name)),
      );
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
      toast({ title: 'Saved', description: 'TaxiCaller settings updated.' });
      await load();
    } catch (err) {
      toast({ title: 'Error', description: (err as Error)?.message || 'Failed to save', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const localeDirty = locale.timezone !== savedLocale.timezone || locale.dateFormat !== savedLocale.dateFormat;

  const handleSaveLocale = async () => {
    setLocaleSaving(true);
    try {
      await saveDateLocaleSettings(locale, DEFAULT_TENANT_ID);
      setDateLocaleCache(locale); // applies app-wide immediately, no reload needed
      setSavedLocale(locale);
      toast({ title: 'Saved', description: 'Time zone & date format updated for the whole app.' });
    } catch (err) {
      toast({ title: 'Error', description: (err as Error)?.message || 'Failed to save', variant: 'destructive' });
    } finally {
      setLocaleSaving(false);
    }
  };

  const navTabs: SettingsSideNavItem[] = useMemo(
    () => [
      { value: INFO_TAB, label: 'Company Info', description: 'Name & company number', icon: Building2 },
      { value: TAXICALLER_TAB, label: 'TaxiCaller API', description: 'Dispatch API credentials', icon: Car },
      { value: LOCALE_TAB, label: 'Time Zone & Dates', description: 'Applies across the whole app', icon: Clock3 },
    ],
    [],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <Card className="shadow-lg bg-card border border-border overflow-hidden">
      <CardHeader className="border-b border-border bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950">
        <div>
          <CardTitle className="text-xl text-primary flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Settings
          </CardTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Company identity, dispatch API credentials, and the time zone &amp; date format used everywhere in the app.
          </p>
        </div>
      </CardHeader>

      <CardContent className="p-0 bg-zinc-950">
        <SettingsSidebarLayout tabs={navTabs} value={active} onValueChange={setActive} minHeight="min-h-[420px]">
          {active === INFO_TAB && (
            <div className="space-y-5 max-w-xl">
              <div>
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-yellow-500" />
                  Company Info
                </h3>
                <p className="text-sm text-muted-foreground mt-1">Used across the app and on payment summaries/reports.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Company Name</Label>
                <Input
                  value={values['ivr.company_name'] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, 'ivr.company_name': e.target.value }))}
                  className="dark-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Company / NZBN Number</Label>
                <Input
                  value={values['company.company_number'] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, 'company.company_number': e.target.value }))}
                  className="dark-input"
                  placeholder="e.g. 9429041234567"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">GST Number</Label>
                <Input
                  value={values['company.gst_number'] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, 'company.gst_number': e.target.value }))}
                  className="dark-input"
                  placeholder="e.g. 123-456-789"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">GST Percentage (%)</Label>
                <p className="text-xs text-gray-500">Used in payroll calculations across the app.</p>
                <Input
                  type="number"
                  step="0.1"
                  value={values['gst_percent'] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, gst_percent: e.target.value }))}
                  className="dark-input"
                />
              </div>
              <Button size="sm" onClick={handleSaveInfo} disabled={saving || !infoDirty}>
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save Changes
              </Button>
            </div>
          )}

          {active === TAXICALLER_TAB && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Car className="h-5 w-5 text-yellow-500" />
                    TaxiCaller API
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Same credentials used by the IVR dispatch integration — editing here updates them everywhere.
                  </p>
                </div>
                <Button size="sm" onClick={handleSaveTaxiCaller} disabled={saving || !taxicallerDirty}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                  Save Changes
                </Button>
              </div>
              {taxicallerRows.length === 0 ? (
                <p className="text-sm text-gray-500">No TaxiCaller settings found yet.</p>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {taxicallerRows.map((row) => {
                    const secret = isSecretField(row.name);
                    const value = values[row.name] ?? '';
                    const shown = revealed[row.name];
                    return (
                      <div key={row.name} className="space-y-1.5">
                        <Label className="text-foreground text-sm">{row.label || row.name}</Label>
                        {row.description && <p className="text-xs text-gray-500">{row.description}</p>}
                        <div className="relative">
                          <Input
                            type={secret && !shown ? 'password' : row.type === 'number' ? 'number' : 'text'}
                            value={value}
                            onChange={(e) => setValues((prev) => ({ ...prev, [row.name]: e.target.value }))}
                            className={`dark-input ${secret ? 'pr-9' : ''}`}
                          />
                          {secret && (
                            <button
                              type="button"
                              onClick={() => setRevealed((prev) => ({ ...prev, [row.name]: !prev[row.name] }))}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                              title={shown ? 'Hide value' : 'Show value'}
                            >
                              {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {active === LOCALE_TAB && (
            <div className="space-y-5 max-w-xl">
              <div>
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <Clock3 className="h-5 w-5 text-yellow-500" />
                  Time Zone &amp; Dates
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Every date and time shown across the app — payment summaries, reports, fleet capacity, driver portal —
                  uses this. Daylight saving is handled automatically for whichever zone you pick.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Time Zone</Label>
                <select
                  value={locale.timezone}
                  onChange={(e) => setLocale((prev) => ({ ...prev, timezone: e.target.value }))}
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
                >
                  {TIMEZONE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-foreground text-sm">Date Format</Label>
                <select
                  value={locale.dateFormat}
                  onChange={(e) => setLocale((prev) => ({ ...prev, dateFormat: e.target.value }))}
                  className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground"
                >
                  {DATE_FORMAT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label} — {o.example}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-md bg-black/30 px-3 py-2 text-xs text-muted-foreground">
                Right now: <span className="text-yellow-500 font-medium">{previewNow(locale)}</span>
              </div>

              <Button size="sm" onClick={handleSaveLocale} disabled={localeSaving || !localeDirty}>
                {localeSaving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                Save Changes
              </Button>
            </div>
          )}
        </SettingsSidebarLayout>
      </CardContent>
    </Card>
  );
};

export default CompanySettings;
